import { useState, useRef } from 'react';
import { apiClient } from '@services/api';
import { compressImage, CompressImageOptions } from '@utils/compressImage';

interface UseImageUploadOptions {
  folder: string;
  compress?: CompressImageOptions;
}

interface UseImageUploadReturn {
  isUploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  triggerFilePicker: () => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>, onSuccess: (url: string) => void) => Promise<void>;
  uploadError: string | null;
}

export function useImageUpload({ folder, compress }: UseImageUploadOptions): UseImageUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const triggerFilePicker = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    onSuccess: (url: string) => void,
  ) => {
    const raw = e.target.files?.[0];
    if (!raw) return;

    e.target.value = '';

    setIsUploading(true);
    setUploadError(null);

    try {
      const { file } = await compressImage(raw, compress ?? { maxWidthOrHeight: 1920, maxSizeMB: 1.0 });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);

      const res = await apiClient.post<{ success: boolean; data: { url: string } }>(
        '/upload/image',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      if (res.data.success && res.data.data?.url) {
        onSuccess(res.data.data.url);
      } else {
        setUploadError('Yükleme başarısız oldu');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? 'Görsel yüklenemedi';
      setUploadError(msg);
    } finally {
      setIsUploading(false);
    }
  };

  return { isUploading, fileInputRef, triggerFilePicker, handleFileChange, uploadError };
}
